import { atom } from 'recoil';

/**
 * Re-render signal for auto-translation - deliberately just an integer.
 *
 * The dictionary itself lives in translationStore (a module-level Map) and is
 * persisted by translationDb (IndexedDB). It used to live in this atom, which
 * meant every merge of a few new translations spread-copied the entire
 * dictionary; that does not survive tag/field/content translation, where an org
 * can reach hundreds of thousands of entries.
 *
 * translationStore bumps its version on every mutation and the loader mirrors
 * that number here, so a write is O(1) and components still repaint.
 * Read it via useTranslationSubscription().
 */
export const translationVersionAtom = atom({
    key: 'translationVersionAtom',
    default: 0
});

/**
 * Signals that the effective display language changed (org toggled 多言語対応, or
 * the user changed their main language) while the page is already open. When
 * true, WrapLayout shows the shared COMMSG030 dialog; the user confirms and the
 * page reloads so every open screen renders consistently in the new language.
 *
 * Set by useTranslationDictionaryLoader on a real transition only - never on the
 * first mount, login, or organization switch.
 */
export const translationReloadDialogAtom = atom({
    key: 'translationReloadDialogAtom',
    default: false
})

/**
 * On-demand content translation state for ticket detail, keyed by ticketId.
 *
 * Used by the on-demand content translation feature for all ticket types. It
 * lives in memory only: translations are kept while navigating panels/tabs but
 * are discarded on browser refresh or logout.
 *
 * Shape:
 * {
 *   [ticketId]: {
 *     ticketType,
 *     status: 'idle' | 'loading' | 'translated' | 'error',  // translation progress
 *     activeView: 'original' | 'translation',               // 「翻訳版」toggle: OFF = original, ON = translation
 *     ticketMode: 'VIEW' | 'EDIT',                          // detail screen mode; EDIT forces original on tab/header/fields
 *     ticketOutline: string,                                // title as rendered by the detail header (FE-computed)
 *     translatedOutline: string|null,                       // translated title, null until ready
 *     fields: {
 *       [fieldKey]: {
 *         sysField,
 *         type,
 *         original: any,           // source value, initialized from ticket content
 *         translated: string|null  // translated value, null until the translation is ready
 *       }
 *     }
 *   }
 * }
 */
export const contentDetailOnDemandAtom = atom({
    key: 'contentDetailOnDemandAtom',
    default: {}
});

/**
 * On-demand chat-thread translation state (CHT-005), keyed by chatThreadId.
 *
 * Unlike contentDetailOnDemandAtom (field-based, per content), a chat thread is a
 * list of messages (ChatPost): the toggle status and its active view live at the
 * thread level (the 「翻訳版」toggle translates the whole thread at once), while each
 * message also keeps its own view so a single message can be flipped independently
 * (§4.2). In-memory only: translations survive panel/tab navigation but are dropped
 * on refresh / logout / switch project.
 *
 * Shape:
 * {
 *   [threadId]: {
 *     toggleStatus: 'idle' | 'loading' | 'translated' | 'error',   // whole-thread 「翻訳版」toggle
 *     activeView: 'original' | 'translation',                      // thread-level view: ON = translation, OFF = original
 *     threadType,                                                  // Constant.THREAD_TYPE
 *     ticketId: string,                                            // ticket the thread belongs to (lets a chat TAB, keyed by ticketId, find the title)
 *     ticketOutline: string,                                       // ticket/knowledge title as rendered by the chat header (FE-computed)
 *     translatedTicketOutline: string|null,                        // translated title, null until ready
 *     messages: {
 *       [postId]: {
 *         postType,                 // Constant.POST_TYPE
 *         creatorId,                // message author (viewer's own text is not translated)
 *         isTranslatable: boolean,  // eligible for translation (spec §5.2/§5.3); drives the toggle link
 *         original: string,         // source content
 *         translated: string|null,  // translated content, null until ready
 *         activeView: 'original' | 'translation',
 *         status: 'idle' | 'loading' | 'translated' | 'error'
 *       }
 *     }
 *   }
 * }
 */
export const chatPostOnDemandAtom = atom({
    key: 'chatPostOnDemandAtom',
    default: {}
});
